/**
 * ClickReel SDK - Cloudflare Stream Player Overlay
 *
 * Embeds Cloudflare Stream videos via iframe with signed playback tokens.
 * Uses the Cloudflare Stream Player API (postMessage) for progress tracking.
 */

import {
  VideoProgressData,
  BubblePosition,
  StylingConfig,
  FloatingAction,
  ProductCardConfig,
  SocialProofConfig,
} from './types';
import { getShadowContainer } from './shadow-dom';
import { ProductCard } from './product-card';
import { injectUtilityStyles, isMobile } from './video-utils';
import { buildCtaUrl } from './analytics-bridge';

export interface CloudflarePlayerOptions {
  signedToken: string;
  videoUid?: string;
  position: BubblePosition;
  styling: StylingConfig;
  onPlay?: () => void;
  onPause?: () => void;
  onProgress?: (data: VideoProgressData) => void;
  onComplete?: () => void;
  onClose?: () => void;
  onCtaClick?: () => void;
  onFloatingActionClick?: (action: FloatingAction) => void;
  ctaText?: string;
  ctaUrl?: string;
  floatingActions?: FloatingAction[];
  muteOnOpen?: boolean;
  productCard?: ProductCardConfig;
  socialProof?: SocialProofConfig;
  storyMode?: boolean;
  storyIndex?: number;
  storyTotal?: number;
  onStoryAdvance?: (direction: 'next' | 'prev') => void;
  showBranding?: boolean;
  estimatedDuration?: number;
  campaignName?: string;
  sessionId?: string;
  videoIdForTracking?: string;
}

export class CloudflarePlayer {
  private overlay: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private options: CloudflarePlayerOptions;
  private _isOpen = false;
  private closing = false;
  private destroyed = false;
  private productCardInstance: ProductCard | null = null;
  private progressInterval: number | null = null;
  private progressStartTime: number = 0;
  private lastQuartile: number = 0;
  private currentTime: number = 0;
  private duration: number = 0;
  private playing = false;
  private muted = false;
  private playPauseBtn: HTMLElement | null = null;
  private muteBtn: HTMLElement | null = null;
  private progressFill: HTMLElement | null = null;
  private timeDisplay: HTMLElement | null = null;

  constructor(options: CloudflarePlayerOptions) {
    this.options = options;
    this.duration = options.estimatedDuration || 0;
  }

  get isVisible(): boolean {
    return this._isOpen;
  }

  async open(): Promise<void> {
    if (this._isOpen) return;

    const shadow = getShadowContainer();
    if (!shadow) {
      throw new Error('Shadow DOM not initialized');
    }

    this.render(shadow);
    this._isOpen = true;
  }

  private render(shadow: ShadowRoot): void {
    injectUtilityStyles(shadow);

    const mobile = isMobile();

    // Overlay background (click-to-close backdrop, sits behind the player)
    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-cloudflare-overlay';
    this.overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 2147483645;
      background: rgba(0,0,0,${mobile ? '1' : '0.4'});
      pointer-events: auto; opacity: 0; transition: opacity 0.3s ease;
    `;

    // Container
    this.container = document.createElement('div');
    this.container.className = 'clickreel-cloudflare-container';

    if (mobile) {
      this.container.style.cssText = `
        position: fixed; inset: 0; width: 100%; height: 100%;
        z-index: 2147483646;
        background: #000; display: flex; flex-direction: column;
      `;
    } else {
      const pos = this.options.position || 'bottom-right';
      const isRight = pos.includes('right');
      const isBottom = pos.includes('bottom');
      this.container.style.cssText = `
        position: fixed;
        ${isBottom ? 'bottom: 24px' : 'top: 24px'};
        ${isRight ? 'right: 24px' : 'left: 24px'};
        z-index: 2147483646;
        width: 380px; max-width: 90vw;
        background: rgba(0,0,0,0.95); border-radius: 16px; overflow: hidden;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        display: flex; flex-direction: column;
      `;
    }

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'clickreel-cloudflare-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = `
      position: absolute; top: ${mobile ? '16px' : '8px'}; right: ${mobile ? '16px' : '8px'};
      z-index: 10; width: ${mobile ? '44px' : '32px'}; height: ${mobile ? '44px' : '32px'};
      border-radius: 50%; border: none;
      background: rgba(0,0,0,0.6); color: white;
      font-size: ${mobile ? '24px' : '20px'}; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      pointer-events: auto; backdrop-filter: blur(4px);
    `;
    closeBtn.addEventListener('click', () => this.close());

    // Story navigation (if multi-video)
    let storyBar: HTMLElement | null = null;
    if (this.options.storyMode) {
      storyBar = this.createStoryBar();
    }

    // Iframe — Cloudflare Stream player
    // Use video UID with iframe.videodelivery.net (universal embed domain)
    // Note: Cloudflare boolean params are presence-based — omit "muted" entirely for unmuted
    // "autoplay" + no "muted" = autoplay with sound (user clicked bubble = user gesture)
    const videoId = this.options.videoUid || this.options.signedToken;
    this.iframe = document.createElement('iframe');
    this.iframe.className = 'clickreel-cloudflare-iframe';
    this.iframe.src = `https://iframe.videodelivery.net/${videoId}?autoplay=true&controls=true&preload=auto`;
    this.iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
    this.iframe.allowFullscreen = true;
    this.iframe.style.cssText = `
      width: 100%; border: none; background: #000; display: block;
      ${mobile ? 'flex: 1; height: 100%;' : 'aspect-ratio: 9/16; max-height: calc(70vh - 100px); min-height: 300px;'}
    `;

    // Listen for Stream Player API messages
    window.addEventListener('message', this.handleStreamMessage);

    // CTA button
    let ctaEl: HTMLElement | null = null;
    if (this.options.ctaText && this.options.ctaUrl) {
      ctaEl = document.createElement('a');
      ctaEl.className = 'clickreel-cloudflare-cta';
      (ctaEl as HTMLAnchorElement).href = buildCtaUrl(this.options.ctaUrl, {
        campaignName: this.options.campaignName,
        videoId: this.options.videoIdForTracking,
        sessionId: this.options.sessionId,
      });
      (ctaEl as HTMLAnchorElement).target = '_blank';
      (ctaEl as HTMLAnchorElement).rel = 'noopener noreferrer';
      ctaEl.textContent = this.options.ctaText;
      const primaryColor = this.options.styling.primaryColor || '#6366f1';
      ctaEl.style.cssText = `
        display: block; text-align: center; padding: 12px 16px; margin: 8px;
        background: ${primaryColor}; color: white; text-decoration: none;
        border-radius: 10px; font-weight: 600; font-size: 14px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      ctaEl.addEventListener('click', () => {
        this.options.onCtaClick?.();
      });
    }

    // Custom controls bar (always visible below the video — CF built-in controls auto-hide in nested iframes)
    const controlsBar = this.createControlsBar();

    // Assemble
    this.container.appendChild(closeBtn);
    if (storyBar) this.container.appendChild(storyBar);
    this.container.appendChild(this.iframe);
    this.container.appendChild(controlsBar);
    if (ctaEl) this.container.appendChild(ctaEl);

    if (mobile) {
      this.overlay.appendChild(this.container);
      shadow.appendChild(this.overlay);
    } else {
      shadow.appendChild(this.overlay);
      shadow.appendChild(this.container);
    }

    // Fade in
    requestAnimationFrame(() => {
      if (this.overlay) this.overlay.style.opacity = '1';
      if (!mobile && this.container) {
        this.container.style.opacity = '1';
        this.container.style.transform = 'translateY(0)';
      }
    });

    // Close on overlay click (desktop only)
    if (!mobile) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Start fallback progress tracking
    this.startProgressTracking();
  }

  private handleStreamMessage = (event: MessageEvent): void => {
    // C4 (2026-04-17): validate the message comes from our own Cloudflare
    // Stream iframe. Without this, any frame on the customer page can forge
    // { type: 'timeupdate', value: 100 } etc. and inflate the intent score.
    // Two checks: event.source ties the message to this iframe instance, and
    // event.origin guards against src navigations.
    if (!this.iframe || event.source !== this.iframe.contentWindow) return;
    if (event.origin !== 'https://iframe.videodelivery.net') return;

    // Cloudflare Stream Player sends messages from iframe.videodelivery.net
    if (!event.data || typeof event.data !== 'object') return;

    const { type, value } = event.data;

    switch (type) {
      case 'play':
        this.playing = true;
        this.options.onPlay?.();
        this.updateControlsUI();
        break;
      case 'pause':
        this.playing = false;
        this.options.onPause?.();
        this.updateControlsUI();
        break;
      case 'timeupdate':
        if (typeof value === 'number') {
          this.currentTime = value;
          this.checkProgress();
          this.updateControlsUI();
        }
        break;
      case 'durationchange':
        if (typeof value === 'number' && value > 0) {
          this.duration = value;
        }
        break;
      case 'ended':
        this.playing = false;
        this.handleComplete();
        this.updateControlsUI();
        break;
    }
  };

  private startProgressTracking(): void {
    this.progressStartTime = Date.now();

    // Fallback: estimate progress from elapsed time if no postMessage events
    this.progressInterval = window.setInterval(() => {
      if (!this.playing || this.duration <= 0) return;

      const elapsed = (Date.now() - this.progressStartTime) / 1000;
      if (this.currentTime === 0 && elapsed > 2) {
        // No postMessage updates - use fallback timing
        this.currentTime = elapsed;
        this.checkProgress();
      }
    }, 1000);
  }

  private checkProgress(): void {
    if (this.duration <= 0) return;

    const pct = (this.currentTime / this.duration) * 100;

    // Fire quartile events
    // E2 (2026-04-17): `as const` narrows each `q` to `25 | 50 | 75 | 100`,
    // matching VideoProgressData.quartile's literal union.
    const quartiles = [25, 50, 75, 100] as const;
    for (const q of quartiles) {
      if (pct >= q && this.lastQuartile < q) {
        this.lastQuartile = q;
        this.options.onProgress?.({
          videoId: this.options.videoIdForTracking ?? '',
          currentTime: this.currentTime,
          duration: this.duration,
          percent: pct,
          quartile: q,
        });
      }
    }

    // Trigger product card
    // E2: align with other players — render(container) to mount, show() to reveal.
    if (this.options.productCard?.enabled && !this.productCardInstance) {
      const triggerPct = this.options.productCard.triggerAtPercent || 50;
      if (pct >= triggerPct) {
        this.productCardInstance = new ProductCard(
          this.options.productCard,
          this.options.styling.primaryColor,
        );
        this.productCardInstance.render(this.container!);
        this.productCardInstance.show();
      }
    }
  }

  private handleComplete(): void {
    // Sprint 002 (2026-04-29): gate quartile-100 emission with lastQuartile guard.
    // Previously the final timeupdate could push pct ≥ 100 (advancing lastQuartile
    // to 100 in checkProgress), and then `ended` would fire handleComplete which
    // emitted quartile 100 again — producing duplicate Meta CAPI Lead syncs.
    if (this.lastQuartile < 100) {
      this.lastQuartile = 100;
      this.options.onProgress?.({
        videoId: this.options.videoIdForTracking ?? '',
        currentTime: this.duration,
        duration: this.duration,
        percent: 100,
        quartile: 100,
      });
    }
    this.options.onComplete?.();

    if (!this.options.storyMode) {
      setTimeout(() => this.close(), 1500);
    }
  }

  private createStoryBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.style.cssText = `
      display: flex; gap: 3px; padding: 8px 12px;
      background: rgba(0,0,0,0.8);
    `;

    const total = this.options.storyTotal || 1;
    const current = this.options.storyIndex || 0;

    for (let i = 0; i < total; i++) {
      const seg = document.createElement('div');
      seg.style.cssText = `
        flex: 1; height: 3px; border-radius: 2px;
        background: ${i < current ? 'white' : i === current ? (this.options.styling.primaryColor || '#6366f1') : 'rgba(255,255,255,0.3)'};
        transition: background 0.3s ease;
      `;
      bar.appendChild(seg);
    }

    // Navigation zones
    if (this.options.onStoryAdvance) {
      const prevZone = document.createElement('div');
      prevZone.style.cssText = `
        position: absolute; left: 0; top: 40px; bottom: 60px; width: 30%;
        z-index: 5; cursor: pointer;
      `;
      prevZone.addEventListener('click', (e) => {
        e.stopPropagation();
        this.options.onStoryAdvance?.('prev');
      });

      const nextZone = document.createElement('div');
      nextZone.style.cssText = `
        position: absolute; right: 0; top: 40px; bottom: 60px; width: 30%;
        z-index: 5; cursor: pointer;
      `;
      nextZone.addEventListener('click', (e) => {
        e.stopPropagation();
        this.options.onStoryAdvance?.('next');
      });

      bar.appendChild(prevZone);
      bar.appendChild(nextZone);
    }

    return bar;
  }

  private createControlsBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'clickreel-cf-controls';
    bar.style.cssText = `
      display: flex; align-items: center; gap: 8px; padding: 8px 12px;
      background: rgba(0,0,0,0.9);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      pointer-events: auto; flex-shrink: 0;
    `;

    // Play/Pause
    this.playPauseBtn = document.createElement('button');
    this.playPauseBtn.innerHTML = '❚❚';
    this.playPauseBtn.style.cssText = `
      background: none; border: none; color: white; font-size: 14px; cursor: pointer;
      width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
      opacity: 0.9; padding: 0;
    `;
    this.playPauseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePlayPause();
    });

    // Progress bar
    const progressContainer = document.createElement('div');
    progressContainer.style.cssText = `
      flex: 1; height: 4px; background: rgba(255,255,255,0.3); border-radius: 2px;
      cursor: pointer; position: relative;
    `;
    this.progressFill = document.createElement('div');
    this.progressFill.style.cssText = `
      height: 100%; background: ${this.options.styling.primaryColor || '#6366f1'}; border-radius: 2px;
      width: 0%; transition: width 0.3s linear;
    `;
    progressContainer.appendChild(this.progressFill);
    progressContainer.addEventListener('click', (e) => {
      e.stopPropagation();
      const rect = progressContainer.getBoundingClientRect();
      const pct = (e.clientX - rect.left) / rect.width;
      if (this.duration > 0) {
        this.sendPlayerCommand('currentTime', pct * this.duration);
      }
    });

    // Time display
    this.timeDisplay = document.createElement('span');
    this.timeDisplay.textContent = '0:00';
    this.timeDisplay.style.cssText = `
      color: rgba(255,255,255,0.8); font-size: 11px; min-width: 36px; text-align: center;
      font-variant-numeric: tabular-nums;
    `;

    // Mute/Unmute
    this.muteBtn = document.createElement('button');
    this.muteBtn.innerHTML = '🔊';
    this.muteBtn.style.cssText = `
      background: none; border: none; color: white; font-size: 16px; cursor: pointer;
      width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
      opacity: 0.9; padding: 0;
    `;
    this.muteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMute();
    });

    bar.appendChild(this.playPauseBtn);
    bar.appendChild(progressContainer);
    bar.appendChild(this.timeDisplay);
    bar.appendChild(this.muteBtn);

    return bar;
  }

  private sendPlayerCommand(method: string, value?: unknown): void {
    if (!this.iframe?.contentWindow) return;
    const msg: Record<string, unknown> = { type: method };
    if (value !== undefined) msg.value = value;
    this.iframe.contentWindow.postMessage(msg, '*');
  }

  private togglePlayPause(): void {
    if (this.playing) {
      this.sendPlayerCommand('pause');
      this.playing = false;
    } else {
      this.sendPlayerCommand('play');
      this.playing = true;
    }
    this.updateControlsUI();
  }

  private toggleMute(): void {
    this.muted = !this.muted;
    this.sendPlayerCommand('muted', this.muted);
    this.updateControlsUI();
  }

  private updateControlsUI(): void {
    if (this.playPauseBtn) {
      this.playPauseBtn.innerHTML = this.playing ? '❚❚' : '▶';
    }
    if (this.muteBtn) {
      this.muteBtn.innerHTML = this.muted ? '🔇' : '🔊';
    }
    if (this.progressFill && this.duration > 0) {
      this.progressFill.style.width = `${(this.currentTime / this.duration) * 100}%`;
    }
    if (this.timeDisplay) {
      const mins = Math.floor(this.currentTime / 60);
      const secs = Math.floor(this.currentTime % 60);
      this.timeDisplay.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }

  close(): void {
    if (!this._isOpen || this.closing) return;
    this.closing = true;

    window.removeEventListener('message', this.handleStreamMessage);

    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    if (this.overlay) {
      this.overlay.style.opacity = '0';
    }
    if (this.container && !isMobile()) {
      this.container.style.opacity = '0';
    }

    setTimeout(() => {
      this.overlay?.remove();
      if (!isMobile()) this.container?.remove();
      this.overlay = null;
      this.container = null;
      this.iframe = null;
      this._isOpen = false;
      this.closing = false;
      this.lastQuartile = 0;
      this.currentTime = 0;
      this.playing = false;
      this.productCardInstance = null;
      this.options.onClose?.();
    }, 300);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    window.removeEventListener('message', this.handleStreamMessage);
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
    this.close();
  }
}
